import React, { useState } from "react";
import { Trash2, Loader2, Plus, Check, Calendar, Flag } from "lucide-react";
import { DatePicker } from "antd";
import dayjs from "dayjs";
import toast from "react-hot-toast";
import { Button, IconButton, Input } from "../../atoms";
import {
  useLeadMilestones, useCreateMilestone, useUpdateMilestone, useDeleteMilestone,
} from "../../hooks/useLeads";
import ConfirmDeleteModal from "../../overlays/ConfirmDeleteModal";
import useFocusRow, { FOCUS_ROW_STYLE } from "../../hooks/useFocusRow";

function fmtDate(d) {
  if (!d) return null;
  return new Date(d).toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" });
}

function MilestoneRow({ leadId, milestone, rowRef }) {
  const update = useUpdateMilestone(leadId);
  const del = useDeleteMilestone(leadId);
  const done = !!milestone.completedAt;
  const overdue = !done && milestone.dueDate && new Date(milestone.dueDate).getTime() < Date.now();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [hovered, setHovered] = useState(false);

  const toggle = async () => {
    try { await update.mutateAsync({ id: milestone._id, patch: { completed: !done } }); }
    catch (err) { toast.error(err?.response?.data?.error || err.message || "Update failed"); }
  };

  const performDelete = async () => {
    try {
      await del.mutateAsync(milestone._id);
      setConfirmOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.error || err.message || "Delete failed");
    }
  };

  return (
    <div
      ref={rowRef}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: "flex", alignItems: "flex-start", gap: 12,
        padding: "12px 14px", borderRadius: 8,
        background: done ? "var(--color-bg-muted)" : "var(--color-bg-surface)",
        border: `1px solid ${done ? "var(--color-border-subtle)" : overdue ? "color-mix(in srgb, var(--crm-danger) 30%, transparent)" : "var(--color-border-subtle)"}`,
        transition: "box-shadow 120ms ease, border-color 120ms ease",
        boxShadow: hovered && !done ? "0 1px 4px rgba(0,0,0,0.06)" : "none",
      }}
    >
      {/* Checkbox */}
      <button
        onClick={toggle}
        disabled={update.isPending}
        aria-label={done ? "Reopen milestone" : "Mark complete"}
        style={{
          width: 20, height: 20, marginTop: 2, flexShrink: 0,
          borderRadius: 6,
          border: `1.5px solid ${done ? "var(--crm-primary)" : overdue ? "var(--crm-danger)" : "var(--color-border-strong, var(--color-border-subtle))"}`,
          background: done ? "var(--crm-primary)" : "transparent",
          color: "white", cursor: update.isPending ? "not-allowed" : "pointer",
          display: "inline-flex", alignItems: "center", justifyContent: "center",
          transition: "background 150ms, border-color 150ms",
        }}
      >
        {update.isPending ? <Loader2 size={11} className="animate-spin" style={{ color: "var(--crm-primary)" }} /> : done && <Check size={12} />}
      </button>

      {/* Content */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontSize: 13, fontWeight: 600,
          color: done ? "var(--color-text-secondary)" : "var(--color-text-primary)",
          textDecoration: done ? "line-through" : "none",
          lineHeight: "20px",
        }}>
          {milestone.title}
        </div>
        {milestone.description && (
          <div style={{ fontSize: 12, color: "var(--color-text-secondary)", marginTop: 3, lineHeight: "18px", whiteSpace: "pre-wrap" }}>
            {milestone.description}
          </div>
        )}
        {milestone.dueDate && (
          <div style={{
            display: "inline-flex", alignItems: "center", gap: 4, marginTop: 5,
            fontSize: 11, fontWeight: 500,
            color: overdue ? "var(--crm-danger)" : done ? "var(--color-text-secondary)" : "var(--color-text-secondary)",
            background: overdue ? "var(--crm-danger-soft)" : "var(--color-bg-muted)",
            padding: "2px 7px", borderRadius: 999,
          }}>
            <Calendar size={10} />
            {fmtDate(milestone.dueDate)}
            {overdue && <span style={{ fontWeight: 600 }}>· Overdue</span>}
            {done && <span>· Done</span>}
          </div>
        )}
      </div>

      {/* Delete — reveals on hover */}
      <div style={{ opacity: hovered ? 1 : 0, transition: "opacity 150ms", flexShrink: 0 }}>
        <IconButton
          aria-label="Delete milestone"
          onClick={() => setConfirmOpen(true)}
          disabled={del.isPending}
          size="sm"
        >
          {del.isPending ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />}
        </IconButton>
      </div>

      <ConfirmDeleteModal
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={performDelete}
        title="Delete milestone?"
        message={`"${milestone.title}" will be removed.`}
        confirmLabel="Delete milestone"
        isPending={del.isPending}
      />
    </div>
  );
}

export default function MilestonesPanel({ leadId, focusItemId, onFocusResolved }) {
  const { data, isLoading } = useLeadMilestones(leadId);
  const create = useCreateMilestone(leadId);
  const { register } = useFocusRow(focusItemId, { onResolved: onFocusResolved });
  const [title, setTitle] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [composerOpen, setComposerOpen] = useState(false);
  const milestones = data?.milestones || [];
  const done = milestones.filter((m) => m.completedAt).length;
  const pct = milestones.length === 0 ? 0 : Math.round((done / milestones.length) * 100);

  const submit = async (e) => {
    e?.preventDefault();
    const t = title.trim();
    if (!t) return;
    try {
      await create.mutateAsync({ title: t, dueDate: dueDate || undefined });
      setTitle(""); setDueDate(""); setComposerOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.error || err.message || "Create failed");
    }
  };

  const cancelComposer = () => { setTitle(""); setDueDate(""); setComposerOpen(false); };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <style>{FOCUS_ROW_STYLE}</style>

      {/* Progress bar — only when milestones exist */}
      {milestones.length > 0 && (
        <div style={{
          background: "var(--color-bg-surface)",
          border: "1px solid var(--color-border-subtle)",
          borderRadius: 8, padding: "12px 14px",
        }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: "var(--color-text-primary)" }}>Progress</div>
            <div style={{ fontSize: 12, color: "var(--color-text-secondary)", fontVariantNumeric: "tabular-nums" }}>
              {done} of {milestones.length} complete · <span style={{ fontWeight: 600, color: pct === 100 ? "var(--crm-success)" : "var(--crm-primary)" }}>{pct}%</span>
            </div>
          </div>
          <div style={{ height: 6, background: "var(--color-bg-muted)", borderRadius: 999, overflow: "hidden" }}>
            <div style={{
              width: `${pct}%`, height: "100%",
              background: pct === 100 ? "var(--crm-success)" : "var(--crm-primary)",
              transition: "width 300ms ease",
              borderRadius: 999,
            }} />
          </div>
        </div>
      )}

      {/* Add milestone button / composer */}
      {!composerOpen ? (
        <Button
          variant="secondary"
          size="sm"
          leftIcon={<Plus size={13} />}
          onClick={() => setComposerOpen(true)}
          style={{ alignSelf: "flex-start" }}
        >
          Add milestone
        </Button>
      ) : (
        <form
          onSubmit={submit}
          style={{
            display: "flex", flexDirection: "column", gap: 10,
            border: "1px solid var(--crm-primary)",
            borderRadius: 8, padding: 14,
            background: "var(--color-bg-surface)",
          }}
        >
          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Milestone title *"
            autoFocus
          />
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Calendar size={13} style={{ color: "var(--color-text-secondary)", flexShrink: 0 }} />
            <DatePicker
              value={dueDate ? dayjs(dueDate) : null}
              onChange={(d) => setDueDate(d ? d.format("YYYY-MM-DD") : "")}
              format="DD MMM YYYY"
              placeholder="Due date (optional)"
              style={{ flex: 1 }}
              allowClear
            />
          </div>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <Button type="button" variant="secondary" size="sm" onClick={cancelComposer} disabled={create.isPending}>
              Cancel
            </Button>
            <Button
              type="submit"
              size="sm"
              disabled={create.isPending || !title.trim()}
              leftIcon={create.isPending ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />}
            >
              Add milestone
            </Button>
          </div>
        </form>
      )}

      {/* List */}
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {isLoading && (
          <div style={{ display: "flex", justifyContent: "center", padding: "32px 0", color: "var(--color-text-secondary)" }}>
            <Loader2 size={18} className="animate-spin" />
          </div>
        )}
        {!isLoading && milestones.length === 0 && !composerOpen && (
          <div style={{
            display: "flex", flexDirection: "column", alignItems: "center", gap: 8,
            padding: "40px 0", color: "var(--color-text-secondary)", textAlign: "center",
          }}>
            <Flag size={28} style={{ opacity: 0.25 }} />
            <div style={{ fontSize: 13, fontWeight: 500, color: "var(--color-text-primary)" }}>No milestones yet</div>
            <div style={{ fontSize: 12 }}>Add steps you need to hit to close this deal.</div>
          </div>
        )}
        {milestones.map((m) => (
          <MilestoneRow key={m._id} leadId={leadId} milestone={m} rowRef={register(m._id)} />
        ))}
      </div>
    </div>
  );
}
